import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Form,
  Table,
  Badge,
  Alert,
  Spinner,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import config from '../config';
import Layout from '../components/layouts/Main';
import { FaPlus, FaSave, FaArrowRight, FaHistory, FaList } from 'react-icons/fa';
import './NewPackingOperations.css';

const NewPackingOperations = () => {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [packedStock, setPackedStock] = useState([]);
  const [activeOperations, setActiveOperations] = useState([]);
  const [completedOperations, setCompletedOperations] = useState([]);
  const [packingMaterials, setPackingMaterials] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [viewMode, setViewMode] = useState('pending'); // 'pending', 'completed', 'stock'

  // Form for initiating packing from stock
  const [initiateForm, setInitiateForm] = useState({
    stockItem: null,
    quantity: '',
    numberOfBags: '',
    outerNumberOfBags: ''
  });

  // Form for finishing packing (adding outputs)
  const [outputForm, setOutputForm] = useState({
    operationId: '',
    inputEntryId: '',
    productName: '',
    productId: '',
    packedQuantity: '',
    packingMaterial: '',
    quantityPerBag: '',
    numberOfBags: '',
    outerPackingMaterial: '',
    outerNumberOfBags: '',
    storageWarehouse: '',
    PMwarehouseLocation: '',
    quantityPerBagUnit: ''
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [productsRes, packingMaterialsRes, warehousesRes, activeOpsRes, completedOpsRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/inventory/all`),
        axios.get(`${config.API_BASE_URL}/packing-materials`),
        axios.get(`${config.API_BASE_URL}/new-warehouses/storage`),
        axios.get(`${config.API_BASE_URL}/packing-operations?status=Pending%20Repack,In%20Progress,Pending`),
        axios.get(`${config.API_BASE_URL}/packing-operations?status=Completed`)
      ]);

      setProducts(productsRes.data.products || []);
      setPackingMaterials(packingMaterialsRes.data.packingMaterials || packingMaterialsRes.data || []);
      setWarehouses(warehousesRes.data || []);
      setActiveOperations(activeOpsRes.data.operations || []);
      setCompletedOperations(completedOpsRes.data.operations || []);
    } catch (error) {
      console.error("Failed to fetch data", error);
      toast.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleProductChange = async (productId) => {
    setSelectedProductId(productId);
    if (!productId) {
      setPackedStock([]);
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`${config.API_BASE_URL}/packing-operations/stock-for-repacking/${productId}`);
      setPackedStock(res.data || []);
    } catch (error) {
      toast.error("Failed to fetch stock for product.");
    } finally {
      setLoading(false);
    }
  };

  const handleInitiatePacking = async () => {
    const { stockItem, quantity, numberOfBags, outerNumberOfBags } = initiateForm;
    if (!stockItem || !quantity) {
      toast.warn("Please select stock and enter quantity.");
      return;
    }

    try {
      await axios.post(`${config.API_BASE_URL}/packing-operations/initiate-repacking`, {
        productId: selectedProductId,
        warehouseId: stockItem.warehouse._id,
        packingMaterialId: stockItem.packingMaterial?._id,
        outerPackingMaterialId: stockItem.outerPackingMaterial?._id,
        quantity: Number(quantity),
        numberOfBags: Number(numberOfBags || 0),
        outerNumberOfBags: Number(outerNumberOfBags || 0),
        purchaseOrderId: stockItem.purchaseOrderId,
        grnId: stockItem.grnId,
        batchNumber: stockItem.batchNumber,
        source: stockItem.source
      });
      toast.success("Stock moved to packing successfully.");
      setInitiateForm({ stockItem: null, quantity: '', numberOfBags: '', outerNumberOfBags: '' });
      setViewMode('pending');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to initiate packing.");
    }
  };

  const handlePMChange = (pmId) => {
    const pm = packingMaterials.find(p => p._id === pmId);
    let qtyPerBag = '';
    let numBags = '';
    let qtyPerBagUnit = '';
    
    if (pm && outputForm.productId) {
      const stuffing = pm.itemWiseStuffing?.find(s => s.product?._id?.toString() === outputForm.productId.toString());
      if (stuffing) {
        qtyPerBag = stuffing.quantity;
        qtyPerBagUnit = stuffing.unit;
        if (outputForm.packedQuantity && qtyPerBag > 0) {
          numBags = Math.ceil(Number(outputForm.packedQuantity) / qtyPerBag);
        }
      }
    }
    
    setOutputForm({
      ...outputForm, 
      packingMaterial: pmId,
      quantityPerBag: qtyPerBag,
      quantityPerBagUnit: qtyPerBagUnit,
      numberOfBags: numBags
    });
  };

  const handleOuterPMChange = (outerPmId) => {
    const outerPm = packingMaterials.find(p => p._id === outerPmId);
    let outerNumBags = '';
    let qtyPerOuterBag = '';

    // if (outerPm && outputForm.numberOfBags && outerPm.outerPackingCapacity > 0) {
    //   outerNumBags = Math.ceil(Number(outputForm.numberOfBags) / outerPm.outerPackingCapacity);
    // }
    if (outerPm && outputForm.productId) {
      const stuffing = outerPm.itemWiseStuffing?.find(s => s.product?._id?.toString() === outputForm.productId.toString());
      if (stuffing) {
        qtyPerOuterBag = stuffing.quantity;
        if (outputForm.packedQuantity && qtyPerOuterBag > 0) {
          outerNumBags = Math.ceil(Number(outputForm.packedQuantity) / qtyPerOuterBag);
        }
      }
    }

    setOutputForm({
      ...outputForm,
      outerPackingMaterial: outerPmId,
      outerNumberOfBags: outerNumBags
    });
  };

  const handleAddOutput = async () => {
    if (!outputForm.operationId || !outputForm.packedQuantity || !outputForm.packingMaterial || !outputForm.storageWarehouse) {
      toast.warn("Please fill all required fields.");
      return;
    }

    try {
      await axios.post(`${config.API_BASE_URL}/packing-operations/${outputForm.operationId}/outputs`, {
        packedOutputs: [{
          inputEntryId: outputForm.inputEntryId,
          product: outputForm.productId,
          packedQuantity: Number(outputForm.packedQuantity),
          packingMaterial: outputForm.packingMaterial,
          quantityPerBag: Number(outputForm.quantityPerBag),
          numberOfBags: Number(outputForm.numberOfBags),
          outerPackingMaterialId: outputForm.outerPackingMaterial || null,
          outerNumberOfBags: Number(outputForm.outerNumberOfBags || 0),
          storageWarehouse: outputForm.storageWarehouse,
          PMwarehouseLocation: outputForm.PMwarehouseLocation || outputForm.storageWarehouse
        }]
      });
      toast.success("Packed output added successfully.");
      setOutputForm({
        operationId: '',
        inputEntryId: '',
        productName: '',
        productId: '',
        packedQuantity: '',
        packingMaterial: '',
        quantityPerBag: '',
        numberOfBags: '',
        outerPackingMaterial: '',
        outerNumberOfBags: '',
        storageWarehouse: '',
        PMwarehouseLocation: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to add output.");
    }
  };

  return (
    <Layout>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/">Dashboard</Link></li>
          <li className="breadcrumb-item active">Packing Operations</li>
        </ol>
      </nav>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2>Packing Operations</h2>
          <small className="text-muted">Manage product packing and repacking processes</small>
        </div>
        <div className="btn-group">
          <Button 
            variant={viewMode === 'pending' ? 'primary' : 'outline-primary'} 
            onClick={() => setViewMode('pending')}
          >
            <FaList className="me-2" /> Pending Packing
          </Button>
          <Button 
            variant={viewMode === 'completed' ? 'success' : 'outline-success'} 
            onClick={() => setViewMode('completed')}
          >
            <FaHistory className="me-2" /> History
          </Button>
          <Button 
            variant={viewMode === 'stock' ? 'info' : 'outline-info'} 
            onClick={() => setViewMode('stock')}
          >
            <FaPlus className="me-2" /> Initiate from Stock
          </Button>
        </div>
      </div>

      {loading && <div className="text-center my-4"><Spinner animation="border" /></div>}

      {!loading && viewMode === 'stock' && (
        <Card className="mb-4">
          <Card.Header className="bg-light">
            <h5 className="mb-0">Step 1: Select Product & Source Stock</h5>
          </Card.Header>
          <Card.Body>
            <Form.Group className="mb-3">
              <Form.Label>Select Product to Pack</Form.Label>
              <Form.Select 
                value={selectedProductId} 
                onChange={(e) => handleProductChange(e.target.value)}
              >
                <option value="">-- Select Product --</option>
                {products.map(p => (
                  <option key={p.product._id} value={p.product._id}>
                    {p.product.productName} ({p.product.productCode})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {selectedProductId && packedStock.length > 0 ? (
              <Table striped bordered hover responsive className="mt-3">
                <thead>
                  <tr>
                    <th>Location</th>
                    <th>Packing Material</th>
                    <th>Outer Packing</th>
                    <th>Available Qty</th>
                    <th>Bags (Inner/Outer)</th>
                    <th>Source/Batch</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {packedStock.map((stock, idx) => (
                    <tr key={idx}>
                      <td>{stock.warehouse?.label || stock.warehouse?.name}</td>
                      <td>{stock.packingMaterial?.name || 'None'}</td>
                      <td>{stock.outerPackingMaterial?.name || 'None'}</td>
                      <td>{stock.availableQuantity}</td>
                      <td>{stock.availableBags} / {stock.availableOuterBags}</td>
                      <td>
                        <Badge bg="info">{stock.source}</Badge> 
                        {stock.batchNumber && <div><small>Batch: {stock.batchNumber}</small></div>}
                      </td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => setInitiateForm({ 
                            ...initiateForm, 
                            stockItem: stock,
                            quantity: stock.availableQuantity,
                            numberOfBags: stock.availableBags,
                            outerNumberOfBags: stock.availableOuterBags
                          })}
                        >
                          Select
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : selectedProductId && !loading ? (
              <Alert variant="info">No packed stock available for this product.</Alert>
            ) : null}

            {initiateForm.stockItem && (
              <div className="mt-4 p-3 bg-light border rounded">
                <h5>Initiate Packing Operation</h5>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Quantity to Pack</Form.Label>
                      <Form.Control 
                        type="number" 
                        value={initiateForm.quantity}
                        onChange={(e) => setInitiateForm({...initiateForm, quantity: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Inner Bags to Empty</Form.Label>
                      <Form.Control 
                        type="number" 
                        value={initiateForm.numberOfBags}
                        onChange={(e) => setInitiateForm({...initiateForm, numberOfBags: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Outer Bags to Empty</Form.Label>
                      <Form.Control 
                        type="number" 
                        value={initiateForm.outerNumberOfBags}
                        onChange={(e) => setInitiateForm({...initiateForm, outerNumberOfBags: e.target.value})}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Button variant="success" className="w-100" onClick={handleInitiatePacking}>
                      Start Packing Process <FaArrowRight className="ms-2" />
                    </Button>
                  </Col>
                </Row>
                <small className="text-muted mt-2 d-block">
                  * Original bags will be returned to Packing Material Stock upon initiation.
                </small>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {!loading && viewMode === 'pending' && (
        <Row>
          <Col lg={7}>
            <Card>
              <Card.Header className="bg-light">
                <h5 className="mb-0">Active Packing Tasks</h5>
              </Card.Header>
              <Card.Body>
                {activeOperations.length > 0 ? (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>OP #</th>
                        <th>Product</th>
                        <th>Location</th>
                        <th>Remaining</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeOperations.map(op => (
                        <tr key={op._id}>
                          <td>{op.operationNumber}</td>
                          <td>{op.inwardEntries[0]?.product?.productName}</td>
                          <td>{op.inwardEntries[0]?.warehouseLocation?.label || op.inwardEntries[0]?.warehouseLocation?.name}</td>
                          <td>{op.inwardEntries.reduce((sum, e) => sum + e.remainingQuantity, 0)}</td>
                          <td>
                            <Button 
                              variant="outline-primary" 
                              size="sm"
                              onClick={() => {
                                const firstEntry = op.inwardEntries[0];
                                setOutputForm({
                                  ...outputForm,
                                  operationId: op._id,
                                  inputEntryId: firstEntry?._id,
                                  productName: firstEntry?.product?.productName,
                                  productId: firstEntry?.product?._id,
                                  packedQuantity: firstEntry?.remainingQuantity
                                });
                              }}
                            >
                              Pack
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <Alert variant="info">No pending packing tasks found.</Alert>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={5}>
            {outputForm.operationId ? (
              <Card>
                <Card.Header className="bg-dark text-white d-flex justify-content-between">
                  <span>New Packing Details</span>
                  <Badge bg="info">{outputForm.productName}</Badge>
                </Card.Header>
                <Card.Body>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Packed Quantity</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={outputForm.packedQuantity}
                          onChange={(e) => setOutputForm({...outputForm, packedQuantity: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Inner Packing Material</Form.Label>
                        <Form.Select 
                          value={outputForm.packingMaterial}
                          onChange={(e) => handlePMChange(e.target.value)}
                        >
                          <option value="">-- Select PM --</option>
                          {packingMaterials.filter(pm => !pm.isOuterPacking).map(pm => (
                            <option key={pm._id} value={pm._id}>{pm.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Qty Per Bag</Form.Label>
                        <div className="position-relative">
                        <Form.Control 
                          type="number" 
                          value={outputForm.quantityPerBag}
                          onChange={(e) => setOutputForm({...outputForm, quantityPerBag: e.target.value})}
                        />
                        <span className="unit-suffix"> {outputForm.quantityPerBagUnit}</span>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Number of Inner Bags</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={outputForm.numberOfBags}
                          onChange={(e) => setOutputForm({...outputForm, numberOfBags: e.target.value})}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />
                  <h6 className="text-muted mb-3">Outer Packing (Optional)</h6>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Outer Packing Material</Form.Label>
                        <Form.Select 
                          value={outputForm.outerPackingMaterial}
                          onChange={(e) => handleOuterPMChange(e.target.value)}
                        >
                          <option value="">-- Select Outer PM --</option>
                          {packingMaterials.filter(pm => pm.isOuterPacking).map(pm => (
                            <option key={pm._id} value={pm._id}>{pm.name}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Number of Outer Bags</Form.Label>
                        <Form.Control 
                          type="number" 
                          value={outputForm.outerNumberOfBags}
                          onChange={(e) => setOutputForm({...outputForm, outerNumberOfBags: e.target.value})}
                          disabled={!outputForm.outerPackingMaterial}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <hr />

                  <Form.Group className="mb-3">
                    <Form.Label>Storage Warehouse</Form.Label>
                    <Form.Select 
                      value={outputForm.storageWarehouse}
                      onChange={(e) => setOutputForm({...outputForm, storageWarehouse: e.target.value})}
                    >
                      <option value="">-- Select Warehouse --</option>
                      {warehouses.map(w => (
                        <option key={w._id} value={w._id}>{w.label}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Button variant="success" className="w-100 py-2" onClick={handleAddOutput}>
                    <FaSave className="me-2" /> Complete Packing & Update Stock
                  </Button>
                </Card.Body>
              </Card>
            ) : (
              <div className="h-100 d-flex align-items-center justify-content-center border rounded bg-light p-5 text-muted">
                Select an operation from the list to start packing
              </div>
            )}
          </Col>
        </Row>
      )}

      {!loading && viewMode === 'completed' && (
        <Card>
          <Card.Header className="bg-light">
            <h5 className="mb-0">Completed Packing Operations</h5>
          </Card.Header>
          <Card.Body>
            {completedOperations.length > 0 ? (
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>OP #</th>
                    <th>Date</th>
                    <th>Product</th>
                    <th>Input Qty</th>
                    <th>Output Details</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {completedOperations.map(op => (
                    <tr key={op._id}>
                      <td>{op.operationNumber}</td>
                      <td>{new Date(op.operationDate).toLocaleDateString()}</td>
                      <td>{op.inwardEntries[0]?.product?.productName}</td>
                      <td>{op.inwardEntries.reduce((sum, e) => sum + e.inputQuantity, 0)}</td>
                      <td>
                        {op.packedOutputs.map((out, idx) => (
                          <div key={idx}>
                            <Badge bg="secondary" className="me-1">{out.packedQuantity} units</Badge>
                            <small>{out.packingMaterial?.name}</small>
                          </div>
                        ))}
                      </td>
                      <td><Badge bg="success">Completed</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info">No completed packing tasks found.</Alert>
            )}
          </Card.Body>
        </Card>
      )}
    </Layout>
  );
};

export default NewPackingOperations;
